// Manipulación pura de la línea de tiempo del tablero (Fase 2).
// Aísla la lógica de edición (añadir/eliminar/reordenar/duraciones) para poder
// testearla con Vitest y reutilizarla desde el editor de timeline.

import { ARROW_KEYS, BoardArrow, BoardJson, BoardTimeline, TimelineEvent, TimelineEventType } from '../interfaces/board-json.interface';
import { DEFAULT_EVENT_DURATION, INTRO_EVENT_ID } from './board-animation';
// La recolección de flechas animables vive en board-animation (la usa también
// buildDefaultTimeline); se re-exporta aquí para no romper los importadores.
export { animatableArrows } from './board-animation';
export type { AnimatableArrow } from './board-animation';

/** Duración mínima de un evento (unidades de tiempo). */
export const MIN_EVENT_DURATION = 0.1;

/** Pausa estándar (segundos) al inicio y final de una animación reproducida, y entre gráficos de una animación completa. */
export const ANIMATION_HOLD = 0.4;

/**
 * Envuelve una línea de tiempo con las pausas estándar de reproducción:
 * una pausa inicial (leading) y una final (trailing) de `holdDuration` segundos.
 * En una concatenación cada gráfico se envuelve antes de unirse (el primero con
 * leading+trailing y el resto solo con trailing), de modo que entre gráficos
 * queda UNA sola pausa de 0.4s en vez de la suma del final de uno y el inicio
 * del siguiente. Elimina el clip intro legacy (INTRO_EVENT_ID) para que las
 * animaciones guardadas antes de retirarlo no acumulen una pausa doble.
 * Los eventos añadidos son de tipo 'hold' sin arrowId; el resto de eventos se
 * conserva y la secuencia se recalcula (los 'parallel' se recomputan igual que
 * en el editor).
 */
export function withAnimationPauses(timeline: BoardTimeline, options: { leading?: boolean; trailing?: boolean; holdDuration?: number } = {}): BoardTimeline {
  const { leading = true, trailing = true, holdDuration = ANIMATION_HOLD } = options;
  // El clip intro legacy ya no forma parte de las timelines nuevas; en las
  // antiguas se descarta para no sumar su pausa a la pausa estándar.
  const events = timeline.events.filter((e) => e.id !== INTRO_EVENT_ID);
  const wrapped: TimelineEvent[] = [];
  if (leading) wrapped.push({ id: 'hold_start', type: 'hold', startTime: 0, duration: holdDuration });
  wrapped.push(...events);
  if (trailing) wrapped.push({ id: 'hold_end', type: 'hold', startTime: 0, duration: holdDuration });
  return recomputeTimeline(wrapped);
}

/**
 * Recalcula la línea de tiempo: por defecto los eventos se encadenan en
 * secuencia (cada uno arranca cuando acaba el anterior); los marcados como
 * 'parallel' arrancan a la vez que el evento anterior. La duración total es el
 * instante más lejano. Devuelve siempre startTime y duration coherentes.
 */
export function recomputeTimeline(events: TimelineEvent[]): BoardTimeline {
  let time = 0;
  const normalized: TimelineEvent[] = [];
  for (const e of events) {
    const duration = Math.max(MIN_EVENT_DURATION, e.duration);
    // Un evento 'parallel' arranca a la vez que el anterior, lo que permite
    // ejecutar varias acciones simultáneas (p. ej. un pase mientras otro
    // jugador se mueve). El resto se encadenan en secuencia.
    const startTime = e.parallel === true && normalized.length > 0 ? normalized[normalized.length - 1].startTime : time;
    const event = { ...e, duration, startTime };
    normalized.push(event);
    // La duración total es el instante más lejano en el que termina un evento.
    time = Math.max(time, startTime + duration);
  }
  return { duration: time, events: normalized };
}

/** Añade un evento al final (ignorando si la flecha ya tiene evento). */
export function addEvent(timeline: BoardTimeline | null, event: TimelineEvent): BoardTimeline {
  const base = timeline?.events ?? [];
  // Solo se evita duplicar eventos con flecha (pases, botes, etc.). Los eventos
  // sin arrowId (pausas, cambios de rol) siempre se añaden.
  if (event.arrowId && base.some((e) => e.arrowId === event.arrowId)) return timeline ?? { duration: 0, events: [] };
  return recomputeTimeline([...base, event]);
}

/**
 * Cambia el tipo de evento de una flecha conservando su posición y duración
 * (p. ej. al alternar entre indicación y acción real desde la barra de
 * herramientas, sin entrar al editor de la línea de tiempo). Si `type` es
 * null, el evento se elimina (la flecha no es animable). Si no existe evento
 * para la flecha, se añade al final con el tipo indicado.
 */
export function setArrowEventType(timeline: BoardTimeline | null, arrowId: string, type: TimelineEventType | null): BoardTimeline {
  const base = timeline?.events ?? [];
  const idx = base.findIndex((e) => e.arrowId === arrowId);
  if (idx >= 0) {
    if (!type) return recomputeTimeline(base.filter((e) => e.arrowId !== arrowId));
    return recomputeTimeline(base.map((e) => (e.arrowId === arrowId ? { ...e, type } : e)));
  }
  if (!type) return timeline ?? { duration: 0, events: [] };
  return addEvent(timeline, {
    id: `ev_${arrowId}`,
    type,
    arrowId,
    startTime: 0,
    duration: DEFAULT_EVENT_DURATION,
  });
}

/** Elimina un evento por su id. */
export function removeEvent(timeline: BoardTimeline, eventId: string): BoardTimeline {
  return recomputeTimeline(timeline.events.filter((e) => e.id !== eventId));
}

/**
 * Elimina de la línea de tiempo los eventos cuyas flechas ya no existen en el
 * tablero (p. ej. tras borrar una flecha con la goma o el clic derecho).
 * Recalcula la secuencia; si no había línea de tiempo devuelve null.
 */
export function pruneTimelineToArrows(timeline: BoardTimeline | null, json: BoardJson): BoardTimeline | null {
  if (!timeline) return null;
  const existing = new Set<string>();
  for (const key of ARROW_KEYS) {
    for (const arrow of json[key] as BoardArrow[]) existing.add(arrow.id);
  }
  // Sujetos existentes (jugadores y defensores) para los eventos de rol: si se
  // borra el elemento, su cambio de rol deja de tener sentido y se purga.
  const subjects = new Set<string>();
  for (const p of json.players) subjects.add(p.id);
  for (const d of json.defenders) subjects.add(d.id);
  // Los eventos sin arrowId (pausas) siempre se conservan; los de rol solo si
  // su sujeto sigue en el tablero.
  const events = timeline.events.filter((e) => {
    if (e.type === 'role') return e.subjectId ? subjects.has(e.subjectId) : true;
    return !e.arrowId || existing.has(e.arrowId);
  });
  if (events.length === timeline.events.length) return timeline;
  return recomputeTimeline(events);
}

/**
 * Rol actual de cada sujeto (jugador/defensor): el rol base del json más el
 * último evento de rol de la línea de tiempo. Permite encadenar varios cambios
 * sobre el mismo elemento (P1 → Defensor → Jugador → …) y saber qué botón
 * ofrece el editor en cada momento.
 */
export function currentRoles(json: BoardJson, timeline: BoardTimeline | null): Map<string, 'player' | 'defender'> {
  const roles = new Map<string, 'player' | 'defender'>();
  for (const p of json.players) roles.set(p.id, 'player');
  for (const d of json.defenders) roles.set(d.id, 'defender');
  for (const e of timeline?.events ?? []) {
    if (e.type === 'role' && e.subjectId && (e.toRole === 'player' || e.toRole === 'defender')) {
      roles.set(e.subjectId, e.toRole);
    }
  }
  return roles;
}

/** Mueve un evento de una posición a otra en la secuencia. */
export function moveEvent(timeline: BoardTimeline, fromIndex: number, toIndex: number): BoardTimeline {
  const events = [...timeline.events];
  const [moved] = events.splice(fromIndex, 1);
  if (!moved) return timeline;
  events.splice(toIndex, 0, moved);
  return recomputeTimeline(events);
}

/** Actualiza la duración de un evento. */
export function updateEventDuration(timeline: BoardTimeline, eventId: string, duration: number): BoardTimeline {
  return recomputeTimeline(timeline.events.map((e) => (e.id === eventId ? { ...e, duration } : e)));
}

/** Posición de una barra del editor visual (porcentajes 0..1 sobre la duración total). */
export interface TimelineBarLayout {
  event: TimelineEvent;
  /** Inicio de la barra en porcentaje de la duración total. */
  left: number;
  /** Ancho de la barra en porcentaje de la duración total. */
  width: number;
}

/**
 * Calcula la posición (left) y el ancho de cada evento como porcentaje de la
 * duración total de la línea de tiempo, para dibujar las barras del editor.
 * El clip intro (pausa inicial invisible en el editor) se excluye: la primera
 * barra visible empieza en el 0s del track, sin hueco, y el total excluye
 * también esa pausa para que no quede un hueco de INTRO_DURATION al final.
 * Función pura y testeable: el track del editor solo la consume. Las
 * duraciones se clampean para que las barras no desborden el carril.
 */
export function layoutTimelineEvents(timeline: BoardTimeline): TimelineBarLayout[] {
  const visible = timeline.events.filter((e) => e.id !== INTRO_EVENT_ID);
  // Origen: el instante del primer evento visible (normalmente el final del
  // intro), para que la primera barra comience en el 0s del track.
  const origin = visible.length > 0 ? Math.min(...visible.map((e) => e.startTime)) : 0;
  // El total excluye el clip de pausa inicial: solo ocupa el inicio, así que
  // restarlo hace que las barras llenen el carril completo (sin hueco final).
  const total = Math.max(MIN_EVENT_DURATION, timeline.duration - origin);
  return visible.map((event) => {
    const left = Math.min(1, Math.max(0, (event.startTime - origin) / total));
    const width = Math.min(1 - left, Math.max(0, event.duration / total));
    return { event, left, width };
  });
}

/** Etiquetas legibles de la UI del editor (tipos de evento, balón y roles). */
export interface TimelineLabels {
  pass: string;
  dribble: string;
  move: string;
  block: string;
  shot: string;
  hold: string;
  role: string;
  indication: string;
  ball: string;
  player: string;
  defender: string;
}

/** Etiquetas por defecto (español). Los consumidores las traducen con TranslateService. */
export const DEFAULT_TIMELINE_LABELS: TimelineLabels = {
  pass: 'Pase',
  dribble: 'Bote',
  move: 'Movimiento',
  block: 'Bloqueo',
  shot: 'Tiro',
  hold: 'Pausa',
  role: 'Rol',
  indication: 'Indicación',
  ball: 'Balón',
  player: 'Jugador',
  defender: 'Defensor',
};

/** Nombres legibles de cada tipo de evento (compat: alias de las etiquetas por defecto). */
export const EVENT_TYPE_LABELS: Record<TimelineEventType, string> = DEFAULT_TIMELINE_LABELS;

/** Etiqueta corta de un elemento (número si es jugador/defensor). */
export function elementLabel(json: BoardJson, id: string, labels: TimelineLabels): string {
  const player = json.players.find((p) => p.id === id);
  if (player) return `P${player.number}`;
  const defender = json.defenders.find((d) => d.id === id);
  if (defender) return `D${defender.number}`;
  if (json.balls.some((b) => b.id === id)) return labels.ball;
  return id;
}

/** Etiqueta legible de un evento (ej: "Pase P5 → P3", "Rol: P1 → Defensor"). */
export function eventLabel(json: BoardJson, event: TimelineEvent, labels: TimelineLabels = DEFAULT_TIMELINE_LABELS): string {
  // Cambio de rol: "Rol: P1 → Defensor" (el sujeto cambia al rol destino).
  if (event.type === 'role') {
    const subject = event.subjectId ? elementLabel(json, event.subjectId, labels) : null;
    const toRole = event.toRole === 'defender' ? labels.defender : labels.player;
    return subject ? `${labels.role}: ${subject} → ${toRole}` : labels.role;
  }
  const source = event.sourceId ? elementLabel(json, event.sourceId, labels) : null;
  const target = event.targetId ? elementLabel(json, event.targetId, labels) : null;
  if (source && target) return `${labels[event.type]} ${source} → ${target}`;
  if (source) return `${labels[event.type]} ${source}`;
  return labels[event.type];
}

/** Etiqueta legible de una flecha animable (para el listado "añadir"). */
export function arrowLabel(json: BoardJson, arrow: BoardArrow, type: TimelineEventType, labels: TimelineLabels = DEFAULT_TIMELINE_LABELS): string {
  return eventLabel(
    json,
    {
      id: arrow.id,
      type,
      arrowId: arrow.id,
      startTime: 0,
      duration: DEFAULT_EVENT_DURATION,
      sourceId: arrow.sourceId,
      targetId: arrow.targetId,
    },
    labels,
  );
}
